import {
  Button,
  Callout,
  DropdownItem,
  DropdownMenu,
  HighlightIcon,
} from '@dev-spendesk/grapes';
import { type PublishedPayableRule } from '@spendesk/bookkeeping-core-types';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { getIconVariantFromName } from 'src/core/modules/bookkeep/settings/integrations/pages/PayableRulesSetupPage/utils';
import { TypeGuardError } from 'src/core/utils/switchGuard';

import {
  useEnrichedPayableAutomationRulesQuery,
  type EnrichedPayableRule,
} from '../../hooks/api/use-enriched-payable-automation-rules-query';
import { type RuleOutput } from '../../models';

import './AutomationConflictSelector.css';

export type Props = {
  selectedRule?: PublishedPayableRule;
  ruleConflict: [RuleOutput, RuleOutput, ...RuleOutput[]];
  onSelect: (ruleOutput: string) => void;
  customTitle?: string;
};

export const AutomationConflictSelector = ({
  selectedRule,
  ruleConflict,
  onSelect,
  customTitle,
}: Props) => {
  const { t } = useTranslation('global');

  const rawRules = ruleConflict.map(({ rule }) => rule);
  const rulesQuery = useEnrichedPayableAutomationRulesQuery(rawRules);
  const rules = unwrapQuery(rulesQuery) ?? rawRules;

  const options = ruleConflict.map(({ rule: { id: ruleId }, output }) => ({
    key: output,
    label: output,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    rule: rules.find(({ id }) => id === ruleId)!,
    output,
  }));

  // Move selected option to the beginning
  const selectedOption =
    selectedRule &&
    options.find((option) => option.rule.id === selectedRule?.id);
  if (selectedOption) {
    options.splice(options.indexOf(selectedOption), 1);
    options.unshift(selectedOption);
  }

  return (
    <Callout
      variant="info"
      className="payable-automation-rules-conflict-callout"
      data-testid="payable-automation-rules-conflict-callout"
      title=<DropdownMenu
        options={options}
        renderButton={(getToggleButtonProps) => (
          <Button
            {...getToggleButtonProps()}
            className="max-w-280px payable-automation-rules-conflict-dropdown-button max-h-680px text-left text-wrap"
            variant="tertiaryNeutral"
            text={
              customTitle ||
              t('expenseInbox.expenseEditor.multipleRulesApply', {
                rules: ruleConflict.length,
              })
            }
          />
        )}
        renderOption={(option) => (
          <DropdownItem
            prefix={
              <HighlightIcon
                className="payable-automation-rules-conflict-icon"
                size={24}
                name={option.rule.icon}
                variant={getIconVariantFromName(option.rule.icon)}
              />
            }
            label={option.rule.name}
            helpText={helpTextForRule(t, option.rule)}
            isSelected={option.rule.id === selectedRule?.id}
          />
        )}
        onSelect={({ output }) => {
          onSelect(output);
        }}
      />
    />
  );
};

export function helpTextForRule(
  t: TGlobalFunctionTyped,
  rule: Pick<EnrichedPayableRule, 'outputs' | 'name'> &
    Partial<Pick<EnrichedPayableRule, 'problems'>>,
) {
  return (Object.entries(rule.outputs) as Entries<typeof rule.outputs>)
    .flatMap((entry) => {
      if (entry === undefined) {
        return [];
      }
      const [field, output] = entry;
      if (!output || typeof output === 'string') {
        return [];
      }

      switch (field) {
        case 'expenseAccount':
          return [
            [t('expenseInbox.expenseEditor.expenseAccount'), output.name],
          ];
        case 'vatAccount':
          return [[t('expenseInbox.expenseEditor.vatAccount'), output.name]];
        case 'costCenter':
          return [
            [t('bookkeep.prepare.payablesEditorForm.costCenter'), output.name],
          ];
        case 'customField':
          return Object.values(output).map((customFieldOutput) => {
            if (
              !customFieldOutput ||
              typeof customFieldOutput === 'string' ||
              typeof customFieldOutput.value === 'string'
            ) {
              return [];
            }
            return [
              customFieldOutput.customField.name,
              `${customFieldOutput.value.name}`,
            ];
          });
        default:
          throw new TypeGuardError(field, `Unexpected output field: ${field}`);
      }
    })
    .map(([fieldLabel, name]) => `${fieldLabel} -> ${name}`)
    .join('; ');
}

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];
